import type { Deal, DealLostReason, ProposalStatus, Role } from '@veem/schema'
import { createRoot } from 'solid-js'
import { createStore, reconcile } from 'solid-js/store'
import { session } from '../../lib/session.ts'
import { catalogActions } from '../../modules/catalog/actions.ts'
import { engineeringActions } from '../../modules/engineering/actions.ts'
import { setEquipmentRepair } from '../../modules/equipment/actions.ts'
import { invoiceActions } from '../../modules/invoices/actions.ts'
import { logisticsActions } from '../../modules/logistics/actions.ts'
import { paymentRequestActions } from '../../modules/payment-requests/actions.ts'
import { productionActions } from '../../modules/production/actions.ts'
import { salesActions } from '../../modules/sales/actions.ts'
import { settingsActions } from '../../modules/settings/actions.ts'
import * as staffActions from '../../modules/staff/actions.ts'
import { taskActions } from '../../modules/tasks/actions.ts'
import { addOrderFile, addOrderNote, removeOrderFile } from '../../modules/tasks/records.ts'
import { warehouseActions } from '../../modules/warehouse/actions.ts'
import type { CustomerKind, CustomerLink, ProtoCustomer, ProtoPerson } from '../data/customers.ts'
import type { InvoiceKind, InvoiceLine, ProtoInvoiceRecord } from '../data/invoices.ts'
import type {
  EngineeringSource,
  FileKind,
  OrderLine,
  PaymentRequestSeed,
  ProductionPriority,
  ProtoFile,
  ProtoOrder,
  ProtoStageRun,
  ProtoTask,
  StandaloneEngineeringJob,
} from '../data/orders.ts'
import type { Person } from '../data/people.ts'
import type { EquipmentRepair, ManualPlanRow, ProtoInternalOrder } from '../data/production.ts'
import type {
  Carrier,
  ProtoShipmentRecord,
  ShipmentCargo,
  ShipmentLine,
} from '../data/shipments.ts'
import type { StockMove } from '../data/stock-moves.ts'
import type { ProtoSupplier, SupplierContact } from '../data/suppliers.ts'
import type { TechCardVersion } from '../data/tech-cards.ts'
import type { ExpectedReceipt, ProtoMaterial, TtnDraft, WarehouseName } from '../data/warehouse.ts'
import type { EngineeringReworkRequest } from './engineering-rework.ts'

export interface PurchaseRequest {
  id: string
  /** Null for a free-text request («що саме» is then in `title`). */
  materialId: string | null
  /** In the material's base unit; for free text, in `unit`. */
  quantity: string
  reason: string
  requestedById: string
  orderNumber: string | null
  createdAt: string
  /** What to buy when it is not a catalogue material. */
  title?: string | null
  unit?: string | null
  /** As typed by the requester, before conversion to the base unit. */
  enteredQuantity?: string | null
  enteredUnit?: string | null
  /** Day (YYYY-MM-DD) the material is needed by. */
  neededBy?: string | null
  /** The customer order it is for, when asked from an order page. */
  dealId?: string | null
  state: 'open' | 'ordered' | 'cancelled'
  /** The supplier order that covers it, once ordered (spec §12.6.2: one need, one supply). */
  supplyOrder?: string
}

/** «Потрібно закупити»: a material in any of its units, or free text with its own unit. */
export interface PurchaseRequestInput {
  materialId: string | null
  title?: string | null
  quantity: string
  /** Omitted means the material's base unit. */
  unit?: string | null
  reason: string
  orderNumber: string | null
  dealId?: string | null
  neededBy?: string | null
}

export type TaskInput = Pick<
  ProtoTask,
  'title' | 'type' | 'priority' | 'notes' | 'assigneeId' | 'dueAt' | 'reminderAt' | 'dealId'
>

export interface CustomerInput {
  kind: CustomerKind
  name: string
  edrpou: string | null
  city: string
  phone: string | null
  email: string | null
  sellerEntityIds: string[]
  note: string | null
  categoryId?: string | null
  contact: { fullName: string; role: string; phone: string | null; email: string | null } | null
  contacts?: { fullName: string; role: string; phone: string | null; email: string | null }[]
}

export interface StaffEvent {
  id: string
  personId: string
  at: string
  actorId: string
  text: string
}

export interface ProtoState {
  customers: ProtoCustomer[]
  persons: ProtoPerson[]
  links: CustomerLink[]
  engineeringJobs: StandaloneEngineeringJob[]
  /** «На доопрацювання» requests from the shop to a named engineer. */
  engineeringReworks: EngineeringReworkRequest[]
  /** Cancelled separate jobs, kept only for the «Скасовано» notice. */
  engineeringCancelled: unknown[]
  orders: ProtoOrder[]
  internalOrders: ProtoInternalOrder[]
  planRows: ManualPlanRow[]
  /** Machines under repair, by equipment id. */
  repairs: Record<string, EquipmentRepair>
  materials: ProtoMaterial[]
  /** Spec §12.1: the append-only stock ledger, newest first. */
  moves: StockMove[]
  /** Ordered deliveries, from order to full receipt. */
  expected: ExpectedReceipt[]
  ttnDrafts: TtnDraft[]
  suppliers: ProtoSupplier[]
  /** Spec §12.8: outbound shipments, owned by logistics. */
  shipments: ProtoShipmentRecord[]
  nextShipmentNumber: number
  /** Spec §11.2: customer invoices with append-only payments. */
  invoices: ProtoInvoiceRecord[]
  nextInvoiceNumber: number
  /** Spec §12.7: every version of every технологічна карта, drafts included. */
  techCards: TechCardVersion[]
  tasks: ProtoTask[]
  paymentRequests: PaymentRequestSeed[]
  purchaseRequests: PurchaseRequest[]
  dismissed: string[]
  /** «Працівники»: who changed whose account and how. Append-only, like every audit trail here. */
  staffLog: StaffEvent[]
  nextOrderNumber: number
  nextDealNumber: number
  nextSupplyNumber: number
}

const buildInitialState = (): ProtoState => ({
  customers: [],
  persons: [],
  links: [],
  engineeringJobs: [],
  engineeringReworks: [],
  engineeringCancelled: [],
  orders: [],
  internalOrders: [],
  planRows: [],
  repairs: {},
  materials: [],
  moves: [],
  expected: [],
  ttnDrafts: [],
  suppliers: [],
  shipments: [],
  nextShipmentNumber: 1,
  invoices: [],
  nextInvoiceNumber: 1,
  techCards: [],
  tasks: [],
  paymentRequests: [],
  purchaseRequests: [],
  dismissed: [],
  staffLog: [],
  nextOrderNumber: 1,
  nextDealNumber: 1,
  nextSupplyNumber: 1,
})

export interface ShipmentInput {
  dealId: string
  lines: ShipmentLine[]
  carrier: Carrier
  carrierName: string | null
  recipient: { name: string; phone: string | null; role: string | null }
  destination: { country: string; city: string; point: string }
  ttn: string | null
  customsDeclaration: string | null
  vehicle: string | null
  places: number | null
  weightKg: string | null
  plannedAt: string
}

export type ManualShipmentInput = Omit<ShipmentInput, 'dealId' | 'lines'> & {
  cargo: ShipmentCargo[]
}

export interface InvoiceInput {
  number: string
  kind: InvoiceKind
  sellerEntityId: string
  customerId: string
  dealId: string | null
  currency: string
  vatName: string
  vatRate: string
  lines: InvoiceLine[]
  dueAt: string
  fileName: string | null
  comment: string | null
}
interface ProtoActions {
  engineeringReleaseConflict: (id: string) => 'draft' | 'stale' | null
  createCustomer: (input: CustomerInput) => Promise<string>
  updateCustomer: (id: string, patch: Partial<ProtoCustomer>, version?: number) => Promise<unknown>
  createCustomerCategory: typeof salesActions.createCustomerCategory
  updateCustomerCategory: typeof salesActions.updateCustomerCategory
  archiveCustomerCategory: typeof salesActions.archiveCustomerCategory
  addContact: (
    customerId: string,
    input: {
      personId?: string
      fullName: string
      role: string
      phone: string | null
      email: string | null
    },
  ) => Promise<unknown>
  updatePerson: (id: string, patch: Partial<ProtoPerson>) => Promise<unknown>
  updateLinkRole: (customerId: string, personIdValue: string, role: string) => Promise<unknown>
  setPrimaryContact: (customerId: string, personIdValue: string) => Promise<unknown>
  detachContact: (customerId: string, personIdValue: string) => Promise<unknown>
  dismiss: (id: string) => Promise<unknown>
  createOrder: (input: {
    name: string
    clientId: string
    contactIds: string[]
    sellerEntityId: string
    currencyCode: string
    lines: Omit<OrderLine, 'id'>[]
    expectedCloseAt: string | null
    internalComment: string
  }) => Promise<string>
  setStage: (dealId: string, stageKey: string) => Promise<unknown>
  loseDeal: (
    dealId: string,
    reason: DealLostReason,
    note: string | null,
    stageKey?: string,
  ) => Promise<unknown>
  reopenDeal: (dealId: string, stageKey: string) => Promise<unknown>
  updateDeal: (
    dealId: string,
    patch: Partial<
      Pick<Deal, 'name' | 'managerId' | 'sellerEntityId' | 'expectedCloseAt' | 'internalComment'>
    >,
    version?: number,
  ) => Promise<unknown>
  toggleDealContact: (dealId: string, personIdValue: string) => Promise<unknown>
  setLines: (dealId: string, lines: OrderLine[], version?: number) => Promise<unknown>
  createProposal: (dealId: string) => Promise<unknown>
  setProposalStatus: (
    dealId: string,
    proposalId: string,
    status: ProposalStatus,
  ) => Promise<unknown>
  addFile: (dealId: string, file: File, kind: FileKind) => Promise<void>
  removeFile: (dealId: string, fileId: string) => Promise<void>
  sendToProduction: (
    dealId: string,
    engineerId: string | null,
    stageKey?: string,
    stageKeys?: string[],
    needsEngineer?: boolean,
  ) => Promise<unknown>
  requestEngineering: (
    dealId: string,
    engineerId: string | null,
    note: string,
    dueAt: string,
    priority?: ProductionPriority,
    files?: File[],
    source?: EngineeringSource,
  ) => Promise<string | null>
  createEngineeringJob: typeof engineeringActions.createEngineeringJob
  addEngineeringFile: typeof engineeringActions.addEngineeringFile
  assignEngineer: typeof engineeringActions.assignEngineer
  startEngineering: typeof engineeringActions.startEngineering
  cancelEngineering: typeof engineeringActions.cancelEngineering
  finishEngineering: typeof engineeringActions.finishEngineering
  completeOwnStage: typeof productionActions.completeOwnStage
  cancelInternalOrder: typeof salesActions.cancelInternalOrder
  setInternalOrderPlan: typeof salesActions.setInternalOrderPlan
  setEngineeringRoute: typeof engineeringActions.setEngineeringRoute
  setEngineeringBom: typeof engineeringActions.setEngineeringBom
  setEngineeringInstructions: typeof engineeringActions.setEngineeringInstructions
  addEngineeringMedia: typeof engineeringActions.addEngineeringMedia
  removeEngineeringMedia: typeof engineeringActions.removeEngineeringMedia
  replaceEngineeringFile: typeof engineeringActions.replaceEngineeringFile
  reloadEngineeringCard: typeof engineeringActions.reloadEngineeringCard
  releaseTechnologyCard: typeof engineeringActions.releaseTechnologyCard
  confirmIssue: (dealId: string) => Promise<string | null>
  stockOperation: (
    input:
      | {
          kind: 'receipt'
          materialId: string
          warehouse: WarehouseName
          location: string
          quantity: string
          entered: { quantity: string; unit: string }
          unitCost: string
          ref: string
          buyerEntityId?: string | null
          files?: File[]
          attachments?: ProtoFile[]
        }
      | {
          kind: 'issue' | 'writeoff'
          materialId: string
          warehouse: WarehouseName
          location: string
          quantity: string
          entered: { quantity: string; unit: string }
          ref: string | null
          reason: string | null
        }
      | {
          kind: 'transfer'
          materialId: string
          warehouse: WarehouseName
          location: string
          to: { warehouse: WarehouseName; location: string }
          quantity: string
          entered: { quantity: string; unit: string }
          reason: string | null
        }
      | {
          kind: 'adjustment'
          materialId: string
          warehouse: WarehouseName
          location: string
          counted: string
          reason: string
        },
    version?: number,
  ) => Promise<string | null>
  createMaterial: (input: {
    name: string
    grade: string | null
    category: ProtoMaterial['category']
    unit: string
    conversions: ProtoMaterial['conversions']
    warehouse: WarehouseName
    location: string
    critical: string
    supplier: string
  }) => Promise<string | null>
  updateMaterial: (
    id: string,
    patch: Pick<
      ProtoMaterial,
      | 'name'
      | 'grade'
      | 'category'
      | 'critical'
      | 'unitCost'
      | 'supplier'
      | 'warehouse'
      | 'location'
      | 'conversions'
    >,
    version?: number,
  ) => Promise<string | null>
  receiveExpected: (
    id: string,
    input: {
      quantity: string
      unit?: string
      warehouse: WarehouseName
      location: string
      document: string
    },
    version?: number,
  ) => Promise<{ toppedUp: number; error?: string }>
  createSupplier: (
    input: Omit<ProtoSupplier, 'id' | 'archivedAt' | 'previousNames' | 'contacts'> & {
      contacts: Omit<SupplierContact, 'id' | 'version' | 'primary'>[]
    },
  ) => Promise<string | null>
  updateSupplier: (
    id: string,
    patch: Partial<Omit<ProtoSupplier, 'id' | 'previousNames'>>,
    version?: number,
  ) => Promise<boolean | null>
  archiveSupplier: (id: string, archived: boolean) => Promise<boolean | null>
  createSupplierGroup: typeof warehouseActions.createSupplierGroup
  updateSupplierGroup: typeof warehouseActions.updateSupplierGroup
  archiveSupplierGroup: typeof warehouseActions.archiveSupplierGroup
  addSupplierContact: typeof warehouseActions.addSupplierContact
  updateSupplierContact: typeof warehouseActions.updateSupplierContact
  setPrimarySupplierContact: typeof warehouseActions.setPrimarySupplierContact
  removeSupplierContact: typeof warehouseActions.removeSupplierContact
  setCriticalLevel: (
    materialId: string,
    critical: string,
    version?: number,
  ) => Promise<boolean | null>
  requestPurchase: (input: PurchaseRequestInput) => Promise<boolean | null>
  placeSupplyOrder: (input: {
    supplier: string
    buyerEntityId: string | null
    lines: { materialId: string; quantity: string; unitCost: string }[]
    warehouse: WarehouseName
    location: string | null
    expectedAt: string | null
    files?: File[]
    invoice: string | null
    requestIds: string[]
    payment: { dueAt: string } | null
  }) => Promise<string | null>
  updateSupplyOrder: (
    number: string,
    input: {
      supplier: string
      expectedAt: string | null
      warehouse: WarehouseName
      invoice: string | null
      files?: File[]
    },
    expectedVersions?: Record<string, number>,
  ) => Promise<string | null>
  requestSupplyPayment: (
    supplyOrder: string,
    input: { invoice: string; dueAt: string; buyerEntityId: string },
    expectedVersions?: Record<string, number>,
  ) => Promise<string | null>
  markSupplyInTransit: (supplyOrder: string) => Promise<boolean | null>
  cancelPurchaseRequest: (id: string) => Promise<boolean | null>
  markPurchaseOrdered: (requestId: string) => Promise<boolean | null>
  assignStage: (dealId: string, stageKey: string, workerId: string) => Promise<boolean>
  planStage: (
    zmId: string,
    stageKey: string,
    patch: Partial<Pick<ProtoStageRun, 'assigneeId' | 'equipmentId' | 'scheduledDate'>>,
  ) => Promise<boolean>
  setProductionPriority: (zmId: string, priority: ProductionPriority) => Promise<boolean>
  addPlanRow: (
    input: Omit<ManualPlanRow, 'id' | 'createdById' | 'createdAt' | 'archivedAt'>,
  ) => Promise<boolean>
  archivePlanRow: (id: string, expectedVersion?: number) => Promise<boolean>
  setEquipmentRepair: (equipmentId: string, reason: string | null) => Promise<boolean>
  createInternalOrder: (input: {
    productName: string
    productId: string | null
    stageKeys: string[]
    quantity: string
    unit: string
    requester: ProtoInternalOrder['requester']
    deadline: string
    priority: ProductionPriority
    note: string | null
    assigneeId?: string | null
    files?: File[]
  }) => Promise<string>
  addProductionProgramFiles: typeof engineeringActions.addProductionProgramFiles
  startStage: (zmId: string, stageKey: string) => Promise<string | null>
  pauseStage: (zmId: string, stageKey: string, reason: string) => Promise<boolean>
  recordOutput: (
    zmId: string,
    stageKey: string,
    entry: { good: string; scrap: string; reason: string | null; note: string | null },
    expectedVersion?: number,
  ) => Promise<boolean>
  finishStage: (zmId: string, stageKey: string, expectedVersion?: number) => Promise<boolean>
  completeStage: (dealId: string, stageKey: string) => Promise<boolean>
  createShipment: typeof logisticsActions.createShipment
  createManualShipment: typeof logisticsActions.createManualShipment
  sendShipment: typeof logisticsActions.sendShipment
  refreshTracking: typeof logisticsActions.refreshTracking
  markDelivered: typeof logisticsActions.markDelivered
  setShipmentTtn: typeof logisticsActions.setShipmentTtn
  cancelShipment: typeof logisticsActions.cancelShipment
  createInvoice: typeof invoiceActions.createInvoice
  markInvoiceSent: typeof invoiceActions.markInvoiceSent
  attachInvoiceFile: typeof invoiceActions.attachInvoiceFile
  recordInvoicePayment: typeof invoiceActions.recordInvoicePayment
  reverseInvoicePayment: typeof invoiceActions.reverseInvoicePayment
  completeTask: (taskId: string) => Promise<unknown>
  reopenTask: (taskId: string) => Promise<unknown>
  cancelTask: (taskId: string) => Promise<unknown>
  updateTask: (taskId: string, patch: Partial<TaskInput>) => Promise<unknown>
  addTask: (input: TaskInput) => Promise<string>
  addNote: (
    dealId: string,
    text: string,
    kind?: 'note' | 'call' | 'email',
    mentions?: string[],
    file?: File,
  ) => Promise<void>
  startTechCardDraft: typeof catalogActions.startTechCardDraft
  updateTechCardDraft: typeof catalogActions.updateTechCardDraft
  addTechCardMedia: typeof catalogActions.addTechCardMedia
  removeTechCardMedia: typeof catalogActions.removeTechCardMedia
  addTechCardFiles: typeof catalogActions.addTechCardFiles
  replaceTechCardFile: typeof catalogActions.replaceTechCardFile
  discardTechCardDraft: typeof catalogActions.discardTechCardDraft
  releaseTechCard: typeof catalogActions.releaseTechCard
  renameWarehouseItem: (
    kind: 'location' | 'category',
    from: string,
    to: string,
    warehouse?: WarehouseName,
  ) => Promise<boolean | null>
  renameWorkCenterInRecords: (from: string, to: string) => Promise<unknown>
  createStaff: (
    input: Pick<
      Person,
      'name' | 'title' | 'email' | 'phone' | 'role' | 'workCenter' | 'workCenters'
    >,
  ) => Promise<{ id: string; password: string }>
  updateStaff: (
    id: string,
    patch: Partial<
      Pick<Person, 'name' | 'title' | 'email' | 'phone' | 'workCenter' | 'workCenters'>
    >,
    expectedVersion?: number,
  ) => Promise<unknown>
  changeStaffRole: (id: string, role: Role, expectedVersion?: number) => Promise<unknown>
  disableStaff: (
    id: string,
    handoverToId: string | null,
    reason: string,
    expectedVersion?: number,
  ) => Promise<unknown>
  enableStaff: (id: string, expectedVersion?: number) => Promise<string | undefined>
  resetStaffPassword: (id: string, expectedVersion?: number) => Promise<string>
  endStaffSessions: (id: string, expectedVersion?: number) => Promise<unknown>
  createProduct: typeof catalogActions.createProduct
  updateProduct: typeof catalogActions.updateProduct
  createPaymentRequest: typeof paymentRequestActions.createPaymentRequest
  transitionPaymentRequest: typeof paymentRequestActions.transitionPaymentRequest
  decidePaymentRequest: typeof paymentRequestActions.decidePaymentRequest
  markPaymentRequestPaid: typeof paymentRequestActions.markPaymentRequestPaid
  undoPaymentRequestPaid: typeof paymentRequestActions.undoPaymentRequestPaid
}

const createProtoStore = () => {
  const [state, setState] = createStore<ProtoState>(buildInitialState())
  const role = () => session()?.actor.role ?? 'worker'
  const personId = () => session()?.actor.id ?? ''
  const orderById = (id: string) => state.orders.find((order) => order.deal.id === id)
  const actions: ProtoActions = {
    engineeringReleaseConflict: (id) => {
      const request =
        orderById(id)?.engineering ?? state.engineeringJobs.find((job) => job.id === id)?.request
      if (!request || request.state === 'done') return null
      const cards = state.techCards.filter((card) => card.productId === request.productId)
      if (cards.some((card) => card.status === 'draft')) return 'draft'
      return (cards.find((card) => card.status === 'released')?.version ?? 0) !==
        request.baseVersion
        ? 'stale'
        : null
    },
    createCustomer: salesActions.createCustomer,
    updateCustomer: salesActions.updateCustomer,
    createCustomerCategory: salesActions.createCustomerCategory,
    updateCustomerCategory: salesActions.updateCustomerCategory,
    archiveCustomerCategory: salesActions.archiveCustomerCategory,
    addContact: salesActions.addContact,
    updatePerson: salesActions.updatePerson,
    updateLinkRole: salesActions.updateLinkRole,
    setPrimaryContact: salesActions.setPrimaryContact,
    detachContact: salesActions.detachContact,
    dismiss: taskActions.dismiss,
    createOrder: salesActions.createOrder,
    setStage: salesActions.setStage,
    loseDeal: salesActions.loseDeal,
    reopenDeal: salesActions.reopenDeal,
    updateDeal: salesActions.updateDeal,
    toggleDealContact: salesActions.toggleDealContact,
    setLines: salesActions.setLines,
    createProposal: salesActions.createProposal,
    setProposalStatus: salesActions.setProposalStatus,
    addFile: addOrderFile,
    removeFile: removeOrderFile,
    sendToProduction: salesActions.sendToProduction,
    requestEngineering: salesActions.requestEngineering,
    createEngineeringJob: engineeringActions.createEngineeringJob,
    addEngineeringFile: engineeringActions.addEngineeringFile,
    assignEngineer: engineeringActions.assignEngineer,
    startEngineering: engineeringActions.startEngineering,
    cancelEngineering: engineeringActions.cancelEngineering,
    finishEngineering: engineeringActions.finishEngineering,
    completeOwnStage: productionActions.completeOwnStage,
    cancelInternalOrder: salesActions.cancelInternalOrder,
    setInternalOrderPlan: salesActions.setInternalOrderPlan,
    setEngineeringRoute: engineeringActions.setEngineeringRoute,
    setEngineeringBom: engineeringActions.setEngineeringBom,
    setEngineeringInstructions: engineeringActions.setEngineeringInstructions,
    addEngineeringMedia: engineeringActions.addEngineeringMedia,
    removeEngineeringMedia: engineeringActions.removeEngineeringMedia,
    replaceEngineeringFile: engineeringActions.replaceEngineeringFile,
    reloadEngineeringCard: engineeringActions.reloadEngineeringCard,
    releaseTechnologyCard: engineeringActions.releaseTechnologyCard,
    confirmIssue: warehouseActions.confirmIssue,
    stockOperation: warehouseActions.stockOperation,
    createMaterial: warehouseActions.createMaterial,
    updateMaterial: warehouseActions.updateMaterial,
    receiveExpected: warehouseActions.receiveExpected,
    createSupplier: warehouseActions.createSupplier,
    updateSupplier: warehouseActions.updateSupplier,
    archiveSupplier: warehouseActions.archiveSupplier,
    createSupplierGroup: warehouseActions.createSupplierGroup,
    updateSupplierGroup: warehouseActions.updateSupplierGroup,
    archiveSupplierGroup: warehouseActions.archiveSupplierGroup,
    addSupplierContact: warehouseActions.addSupplierContact,
    updateSupplierContact: warehouseActions.updateSupplierContact,
    setPrimarySupplierContact: warehouseActions.setPrimarySupplierContact,
    removeSupplierContact: warehouseActions.removeSupplierContact,
    setCriticalLevel: warehouseActions.setCriticalLevel,
    requestPurchase: warehouseActions.requestPurchase,
    placeSupplyOrder: warehouseActions.placeSupplyOrder,
    updateSupplyOrder: warehouseActions.updateSupplyOrder,
    requestSupplyPayment: warehouseActions.requestSupplyPayment,
    markSupplyInTransit: warehouseActions.markSupplyInTransit,
    cancelPurchaseRequest: warehouseActions.cancelPurchaseRequest,
    markPurchaseOrdered: warehouseActions.markPurchaseOrdered,
    assignStage: productionActions.assignStage,
    planStage: productionActions.planStage,
    setProductionPriority: productionActions.setProductionPriority,
    addPlanRow: productionActions.addPlanRow,
    archivePlanRow: productionActions.archivePlanRow,
    setEquipmentRepair,
    createInternalOrder: salesActions.createInternalOrder,
    addProductionProgramFiles: engineeringActions.addProductionProgramFiles,
    startStage: productionActions.startStage,
    pauseStage: productionActions.pauseStage,
    recordOutput: productionActions.recordOutput,
    finishStage: productionActions.finishStage,
    completeStage: productionActions.completeStage,
    createShipment: logisticsActions.createShipment,
    createManualShipment: logisticsActions.createManualShipment,
    sendShipment: logisticsActions.sendShipment,
    refreshTracking: logisticsActions.refreshTracking,
    markDelivered: logisticsActions.markDelivered,
    setShipmentTtn: logisticsActions.setShipmentTtn,
    cancelShipment: logisticsActions.cancelShipment,
    createInvoice: invoiceActions.createInvoice,
    markInvoiceSent: invoiceActions.markInvoiceSent,
    attachInvoiceFile: invoiceActions.attachInvoiceFile,
    recordInvoicePayment: invoiceActions.recordInvoicePayment,
    reverseInvoicePayment: invoiceActions.reverseInvoicePayment,
    completeTask: taskActions.completeTask,
    reopenTask: taskActions.reopenTask,
    cancelTask: taskActions.cancelTask,
    updateTask: taskActions.updateTask,
    addTask: taskActions.addTask,
    addNote: addOrderNote,
    startTechCardDraft: catalogActions.startTechCardDraft,
    updateTechCardDraft: catalogActions.updateTechCardDraft,
    addTechCardMedia: catalogActions.addTechCardMedia,
    removeTechCardMedia: catalogActions.removeTechCardMedia,
    addTechCardFiles: catalogActions.addTechCardFiles,
    replaceTechCardFile: catalogActions.replaceTechCardFile,
    discardTechCardDraft: catalogActions.discardTechCardDraft,
    releaseTechCard: catalogActions.releaseTechCard,
    renameWarehouseItem: warehouseActions.renameWarehouseItem,
    renameWorkCenterInRecords: settingsActions.renameWorkCenterInRecords,
    createStaff: staffActions.createStaff,
    updateStaff: staffActions.updateStaff,
    changeStaffRole: staffActions.changeStaffRole,
    disableStaff: staffActions.disableStaff,
    enableStaff: staffActions.enableStaff,
    resetStaffPassword: staffActions.resetStaffPassword,
    endStaffSessions: staffActions.endStaffSessions,
    createProduct: catalogActions.createProduct,
    updateProduct: catalogActions.updateProduct,
    createPaymentRequest: paymentRequestActions.createPaymentRequest,
    transitionPaymentRequest: paymentRequestActions.transitionPaymentRequest,
    decidePaymentRequest: paymentRequestActions.decidePaymentRequest,
    markPaymentRequestPaid: paymentRequestActions.markPaymentRequestPaid,
    undoPaymentRequestPaid: paymentRequestActions.undoPaymentRequestPaid,
  }
  return {
    state,
    role,
    personId,
    orderById,
    actions,
    hydrate: (snapshot: Partial<ProtoState>) =>
      setState(reconcile({ ...buildInitialState(), ...snapshot })),
  }
}
export const proto = createRoot(createProtoStore)
